import { useState } from 'react';

interface HeroProps {
  onContactClick: () => void;
}

export default function Hero({ onContactClick }: HeroProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Navigation */}
      <nav className="relative z-50 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img 
            src="https://static.readdy.ai/image/9bbaefa2db83d27f3b99b300a515e0ca/030dfe57d5d838db27faa02dd2828490.png" 
            alt="EasyTravel Logo" 
            className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
          />
          <span className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: '"Inter", "Helvetica Neue", sans-serif' }}>
            EasyTravel
          </span>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <a href="#usp" className="text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            Voordelen
          </a>
          <a href="#workflow" className="text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            Proces
          </a>
          <a href="#architectuur" className="text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            Technologie
          </a>
          <a href="#cases" className="text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            Cases
          </a>
          <button
            onClick={onContactClick}
            className="bg-teal-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Contact
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-lg transition-all duration-200 cursor-pointer z-50 relative"
          aria-label="Menu"
        >
          <div className="relative w-6 h-6 flex items-center justify-center">
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl transition-all duration-200`}></i>
          </div>
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={closeMobileMenu}>
          <div 
            className="absolute top-0 right-0 w-80 max-w-[90vw] h-full bg-slate-900 shadow-2xl transform transition-transform duration-300 ease-out"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pt-20 px-6 py-8 space-y-6">
              <a 
                href="#usp" 
                onClick={closeMobileMenu}
                className="block text-gray-300 hover:text-white transition-colors cursor-pointer py-3 text-lg border-b border-gray-700/50"
              >
                Voordelen
              </a>
              <a 
                href="#workflow" 
                onClick={closeMobileMenu}
                className="block text-gray-300 hover:text-white transition-colors cursor-pointer py-3 text-lg border-b border-gray-700/50"
              >
                Proces
              </a>
              <a 
                href="#architectuur" 
                onClick={closeMobileMenu}
                className="block text-gray-300 hover:text-white transition-colors cursor-pointer py-3 text-lg border-b border-gray-700/50"
              >
                Technologie
              </a>
              <a 
                href="#cases" 
                onClick={closeMobileMenu}
                className="block text-gray-300 hover:text-white transition-colors cursor-pointer py-3 text-lg border-b border-gray-700/50"
              >
                Cases
              </a>
              <button
                onClick={() => {
                  onContactClick();
                  closeMobileMenu();
                }}
                className="w-full bg-teal-500 text-white px-6 py-4 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap mt-8 text-lg"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-24 xl:py-32">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              EasyTravel: Uw Platform voor Onafhankelijke Regie in Groepsvervoer
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-3 sm:mb-4 leading-relaxed">
              Behoud de controle. EasyTravel ontsluit 24/7 realtime data, onafhankelijk van elke vervoerder.
            </p>
            <p className="text-base sm:text-lg text-gray-400 mb-6 sm:mb-8 leading-relaxed">
              Of je nu een vervoerder bent of een gemeente, EasyTravel biedt een krachtige en betrouwbare oplossing voor het berekenen van taxiritten. Snel en eenvoudig tarieven bepalen zonder onduidelijkheid tussen partijen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={onContactClick}
                className="bg-teal-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Vraag Demo Aan
              </button>
              <a
                href="#workflow"
                className="bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap border border-white/20 text-center"
              >
                Ontdek de Modules
              </a>
            </div>
          </div>

          {/* Dashboard Visualization */}
          <div className="relative mt-8 lg:mt-0">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">Realtime Dashboard</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                  <span className="text-xs sm:text-sm text-gray-600">Live</span>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-3 sm:p-4 rounded-lg">
                  <div className="text-xs sm:text-sm text-teal-700 mb-1">Geplande Ritten</div>
                  <div className="text-xl sm:text-3xl font-bold text-teal-900">247</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 sm:p-4 rounded-lg">
                  <div className="text-xs sm:text-sm text-blue-700 mb-1">Gerealiseerd</div>
                  <div className="text-xl sm:text-3xl font-bold text-blue-900">243</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 sm:p-4 rounded-lg">
                  <div className="text-xs sm:text-sm text-green-700 mb-1">Op Tijd</div>
                  <div className="text-xl sm:text-3xl font-bold text-green-900">98.4%</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-3 sm:p-4 rounded-lg">
                  <div className="text-xs sm:text-sm text-amber-700 mb-1">Meldingen</div>
                  <div className="text-xl sm:text-3xl font-bold text-amber-900">3</div>
                </div>
              </div>

              {/* Chart Visualization */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs sm:text-sm font-semibold text-gray-700">Prestaties Deze Week</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-gray-500">Live data</span>
                  </div>
                </div>
                <div className="flex items-end justify-between h-24 sm:h-32 space-x-1">
                  {[
                    { height: 68, day: 'Ma', value: '247' },
                    { height: 74, day: 'Di', value: '268' },
                    { height: 62, day: 'Wo', value: '225' },
                    { height: 76, day: 'Do', value: '275' },
                    { height: 70, day: 'Vr', value: '255' },
                    { height: 78, day: 'Za', value: '285' },
                    { height: 75, day: 'Zo', value: '270' }
                  ].map((bar, index) => (
                    <div key={index} className="flex-1 flex flex-col justify-end group relative">
                      <div
                        className="bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-sm transition-all duration-500 ease-out hover:from-teal-600 hover:to-teal-500 cursor-pointer shadow-sm min-h-[8px] w-full"
                        style={{ 
                          height: `${Math.max(bar.height * 0.75, 16)}px`,
                          animationDelay: `${index * 150}ms`
                        }}
                      >
                        {/* Tooltip */}
                        <div className="absolute -top-8 sm:-top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {bar.value} ritten
                        </div>
                        
                        {/* Animated highlight */}
                        <div className="absolute inset-0 bg-white/20 rounded-t-sm opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-600">
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Ma</span>
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Di</span>
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Wo</span>
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Do</span>
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Vr</span>
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Za</span>
                  <span className="hover:text-teal-600 transition-colors cursor-pointer">Zo</span>
                </div>
                
                {/* Trend indicator */}
                <div className="flex items-center justify-center mt-3 space-x-2">
                  <div className="flex items-center space-x-1 text-green-600">
                    <i className="ri-arrow-up-line text-sm"></i>
                    <span className="text-xs font-semibold">+12.5%</span>
                  </div>
                  <span className="text-xs text-gray-500">vs vorige week</span>
                </div>
              </div>
            </div>

            {/* Floating indicators */}
            <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 bg-teal-500 text-white px-3 sm:px-4 py-2 rounded-lg shadow-lg">
              <div className="text-xs">Realtime Monitoring</div>
              <div className="text-sm sm:text-lg font-bold">Actief</div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
    </div>
  );
}
